<template>
  <div class="grid-wrapper">

    <elhub-popup-wizard
      :visible.sync="actionDialog.visible"
      :type="actionDialog.type"
      :page.sync="actionDialog.page"
      :selection="selection"
      @confirm="onActionDialogConfirm"
      @cancel="onActionDialogCancel"
      @send-mail="onActionDialogSendMail"
    />

    <div class="grid-toolbar-wrapper">
      <div class="grid-toolbar-buttons-wrapper" v-if="isEmployee">
        <button
          class="btn btn-sm btn-outlined text-elhub"
          :title="requestAccessBtnTooltipString"
          :class="{'btn-disabled': !selection.requestAccessRows.length || requestAccessRowsMaxLimitReached}"
          :disabled="!selection.requestAccessRows.length || requestAccessRowsMaxLimitReached"
          @click="onAddMetersBtnClick"
        >
          {{selectionRequestAccessBtnString}}
        </button>
        <button
          class="btn btn-sm btn-outlined text-error"
          :title="revokeAccessBtnTooltipString"
          :class="{'btn-disabled': !selection.revokeAccessRows.length || revokeAccessRowsMaxLimitReached}"
          :disabled="!selection.revokeAccessRows.length || revokeAccessRowsMaxLimitReached"
          @click="onRevokeMetersBtnClick"
        >
          {{selectionRevokeAccessBtnString}}
        </button>
        <button
          class="btn btn-sm btn-xs text-primary"
          title="Send mail til kunde"
          :class="{'btn-disabled': !selection.revokeAccessRows.length || revokeAccessRowsMaxLimitReached}"
          :disabled="!selection.revokeAccessRows.length || revokeAccessRowsMaxLimitReached"
          @click="onSendMailBtnClick"
        >
          <i class="icon dx-icon-email"></i>
        </button>
      </div>
      <span class="totals-count-wrapper">
        [
        <span>Målere i utvalg: </span>
        <span class="totals-count-value">{{totalsCountString}}</span>
        ]
      </span>
    </div>

    <dx-data-grid
      no-data-text="Ingen data i resultat"
      :data-source="dataSource"
      :allow-column-resizing="true"
      :show-borders="true"
      :show-column-lines="true"
      :show-row-lines="true"
      :row-alternation-enabled="true"
      :ref="refNames.gridRefName"
      :filter-value="computedFilter"
      @cell-click="onCellClick"
      @content-ready="onGridContentReady"
      @selection-changed="onSelectionChanged"
    >
      <!-- Filter-only columns -->
      <dx-column
        data-field="eosContact"
        data-type="string"
        caption="EOS-ansvarlig"
        :visible="showFilterColumns"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="customerContact"
        data-type="string"
        caption="Kundeansvarlig"
        :visible="showFilterColumns"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="eosSupplier"
        data-type="string"
        caption="EOS-leverandør"
        :visible="showFilterColumns"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="billingContact"
        data-type="string"
        caption="Faktura/abb.ansvarlig"
        :visible="showFilterColumns"
        :allow-editing="false"
      >
      </dx-column>

      <!-- Data columns -->
      <dx-column
        data-field="customerName"
        data-type="string"
        caption="Kunde"
        :allow-editing="false"
        :visible="isEmployee"
      >
      </dx-column>
      <dx-column
        data-field="buildingName"
        data-type="string"
        caption="Bygg"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="subscriptionName"
        data-type="string"
        caption="Måler"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="subscriptionStatusText"
        data-type="string"
        caption="Målepunkt status"
        :allow-editing="false"
        :width="160"
        :visible="isEmployee"
      >
      </dx-column>
      <dx-column
        data-field="ean"
        data-type="string"
        caption="MålepunktID"
        alignment="center"
        :width="180"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="legalOwnerName"
        data-type="string"
        caption="Juridisk eier"
        alignment="center"
        :width="180"
        :visible="showLegalOwner"
        :allow-editing="false"
      >
      </dx-column>
      <dx-column
        data-field="thirdPartyAccessStatusText"
        data-type="string"
        caption="Status"
        alignment="center"
        cell-template="statusRowTemplate"
        css-class="grid-third-party-status-column"
        :allow-editing="false"
        :width="180"
      >
      </dx-column>
      <dx-column
        data-field="thirdPartyAccessTime"
        data-type="date"
        caption="Dato"
        alignment="center"
        :allow-editing="false"
        :width="120"
      >
      </dx-column>
      <dx-column
        data-field="thirdPartyAccessWanted"
        data-type="boolean"
        caption="Elhub"
        alignment="center"
        :allow-editing="false"
        :width="100"

      >
      </dx-column>

      <dx-search-panel
        placeholder="Søk"
        :text.sync="_searchText"
        :visible="true"
        :highlight-case-sensitive="false"
        :width="300"
      />

      <dx-scrolling mode="virtual"/>

      <dx-header-filter
        :visible="true"
        :allow-search="true"
        :height="370"
      />

      <dx-editing
        :allow-updating="true"
        :allow-deleting="false"
        :allow-adding="false"
        mode="cell"
      />

      <dx-selection
        select-all-mode="allPages"
        show-check-boxes-mode="always"
        mode="multiple"
        v-if="isEmployee"
      />

      <dx-export
        file-name="elhub-status"
        color="hsl(120, 87%, 37%);"
        :enabled="true"
        :allowExportSelectedData="selection.count > 0"
        :excel-filter-enabled="true"
      />

      <div slot="statusRowTemplate" slot-scope="data">
        <span class="status-row-template">{{data.value}}</span>
      </div>
    </dx-data-grid>

    <transition name="slide">
      <div class="grid-empty-result-wrapper" v-show="isEmptyEANSearch">
        <div class="grid-empty-result-container">
          <table>
            <tr>
              <td>
                <i class="far fa-surprise grid-empty-result-icon"></i>
              </td>
              <td>
                <strong>Finner du ikke måleren du søker på?</strong>
                <div
                  @click="onSearchByEanBtnClick"
                  class="grid-empty-result-link-wrapper"
                >
                  <span>Trykk for utvidet søk: </span>
                  <span class="grid-empty-result-link">{{this.searchText}}</span>
                </div>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </transition>

    <elhub-popover
      :model="historyPopover"
      :ref="refNames.historyPopoverRefName"
    >
      <dx-scroll-view
        :height="138"
        :show-scrollbar="'always'"
      >
      <table class="history-popup-table" v-show="!historyPopover.loaderVisible && historyPopover.data.length">
        <thead>
        <tr>
          <th>Dato</th>
          <th>Svardato</th>
          <th>Handling</th>
          <th>Status</th>
          <th>Beskrivelse</th>
          <th>Feilkode</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in historyPopover.data">
          <td class="datetime">{{getDateString(row.requestTime)}}</td>
          <td class="datetime">{{getDateString(row.responseTime)}}</td>
          <td>
            <strong style="margin-right: 6px;">
              {{row.type}}
            </strong>
          </td>
          <td>
            <strong style="margin-right: 12px;" :class="{'color-success': row.state === 'SUCCESS', 'color-failure': row.state === 'DELETE' || row.state === 'FAIL'}">
              {{row.state}}
            </strong>
          </td>
          <td>
            <span>{{getHistoryStatusTextByState(row)}}</span>
          </td>
          <td>
            <a v-if="row.errorCode"
               target="_blank"
               class="color-link"
               :href="'https://dok.elhub.no/ediel1141/error-codes-and-messages#Errorcodesandmessages-' + row.errorCode">
              {{row.errorCode}}
            </a>
          </td>
        </tr>
        </tbody>
      </table>
      </dx-scroll-view>
    </elhub-popover>

    <elhub-popover
      :model="eanSearchPopover"
      :ref="refNames.eanSearchPopoverRefName"
    >
      <table class="history-popup-table">
        <thead>
        <tr>
          <th>Kunde</th>
          <th>Bygg</th>
          <th>Abb.navn</th>
          <th>Status</th>
          <th>Målernr</th>
          <th>Kundekontakt</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in eanSearchPopover.data">
          <td :title="row.customerId">{{row.customerName}}</td>
          <td :title="row.buildingId">{{row.buildingName}}</td>
          <td :title="row.subscriptionId">{{row.subscriptionName}}</td>
          <td>{{row.subscriptionStatus}}</td>
          <td>{{row.meterNumber}}</td>
          <td>{{row.customerContact}}</td>
        </tr>
        </tbody>
      </table>
    </elhub-popover>

  </div>
</template>

<script>

  // External libs
  import CustomStore from 'devextreme/data/custom_store';
  import DxPopover from 'devextreme-vue/popover';
  import DxLoadIndicator from 'devextreme-vue/load-indicator';
  import DxScrollView from 'devextreme-vue/scroll-view';

  import {dateService} from '@entro-no/common';

  import {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxHeaderFilter,
    DxScrolling,
    DxEditing,
    DxSelection,
    DxExport,
  } from 'devextreme-vue/data-grid';

  // Internal libs
  import ElhubPopupWizard from './ElhubPopupWizard';
  import ElhubPopover from './ElhubPopover';

  // Services
  import MeterStatusService from '../services/elhub.meterstatus.service';

  // Models
  import {ElhubPopoverModel} from '../models/ElhubPopoverModel';

  const vueComponents = {
    DxPopover,
    DxLoadIndicator,
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxHeaderFilter,
    DxScrolling,
    DxEditing,
    DxSelection,
    DxExport,
    DxScrollView,
    ElhubPopupWizard,
    ElhubPopover
  };

  const enumActionType = MeterStatusService.enumActionType;

  function getAccessStatusTranslation(value) {
    return MeterStatusService.enumMeterAccessStatus[value] || value || MeterStatusService.enumMeterAccessStatus.DEFAULT;
  }

  const enumSubscriptionStatus = {
    ACTIVE: 'Aktiv',
    INACTIVE_CUSTOMER: 'Inaktiv kunde',
    BUILDING_SOLD: 'Solgt bygg',
    SUBSCRIPTION_CLOSED: 'Stengt abonnement',
    SUBSCRIPTION_NOT_CONTROLLED: 'Ikke EOS/fakt.kontroll',
    NO_ACTIVE_METER: 'Ingen aktive målere'
  };

  function loadData(data) {
    return (data ?
        Promise.resolve(data) :
        MeterStatusService.getData()
    ).then(data => {
      return data.map((row, index) => {

        row.id = row.subscriptionId;
        row.thirdPartyAccessStatusText = getAccessStatusTranslation(row.thirdPartyAccessStatus);
        row.subscriptionStatusText = enumSubscriptionStatus[row.subscriptionStatus];

        if (row.thirdPartyAccessWanted == null) {
          row.thirdPartyAccessWanted = false;
        }

        if (row.thirdPartyAccessStatus === 'OK' && row.thirdPartyAccessWanted === false) {
          row.thirdPartyAccessStatusText = MeterStatusService.enumMeterAccessStatus.DEFAULT;
        }

        return row;
      });
    });
  }

  function getDataSource(data) {
    let dataSourceData = null;

    return {
      store: new CustomStore({
        key: ['id'],
        async load() {
          if (dataSourceData === null) dataSourceData = await loadData(data);
          return dataSourceData;
        }
      })
    };
  }

  function setTotalsInSelection(totalsObj, grid) {
    if (!totalsObj || !grid) return;

    const totalsInSelectionCount = grid.totalCount();
    const dataSource = grid.getDataSource();

    if (dataSource != null) {
      totalsObj.inSelection = totalsInSelectionCount;
    }
  }

  function setConfirmActionDialogType(actionType, _this) {
    _this.actionDialog.type = actionType;
  }

  function showConfirmActionDialog(_this) {
    _this.actionDialog.visible = true;
  }

  function hideConfirmActionDialog(_this) {
    _this.actionDialog.visible = false;
  }

  function getSelectionTypeData(_this) {
    switch (_this.actionDialog.type) {
      case enumActionType.REQUEST: return _this.selection.requestAccessRows;
      case enumActionType.REVOKE: return _this.selection.revokeAccessRows;
      default: return null;
    }
  }

  function generateListOfDuplicateMeters(_this) {
    _this.selection.duplicateRows = [];

    const data = getSelectionTypeData(_this);

    const uniqueEanIds = [...new Set(data.map(row => row.ean))];
    const groupedMetersByEAN = _this.data
      .filter(row => uniqueEanIds.indexOf(row.ean) !== -1)
      .reduce((acc, row) => {
        const pointer = acc[row.ean];
        if (!pointer) acc[row.ean] = [row];
        else pointer.push(row);

        return acc;
      }, {});

    _this.selection.duplicateRows = Object.keys(groupedMetersByEAN)
      .map(key => groupedMetersByEAN[key])
      .filter(rows => rows.length > 1);
  }

  function updateSelectionLists(_this) {
    const grid = _this.grid;
    const selectedRowsData = grid.getSelectedRowsData();

    _this.selection.count              = selectedRowsData.length;
    _this.selection.requestAccessRows  = selectedRowsData.filter(row => !row.thirdPartyAccessWanted || row.thirdPartyAccessStatus === 'STOPPED');
    _this.selection.revokeAccessRows   = selectedRowsData.filter(row => row.thirdPartyAccessWanted);
  }

  function reloadDataSource(_this) {
    _this.$emit('update:data');
  }

  const refNames = {
    gridRefName:             'elhubGrid',
    historyPopoverRefName:   'historyPopoverRefName',
    eanSearchPopoverRefName: 'elhubEanSearchPopover'
  };

  export default {
    name: 'ElhubMeterStatusGrid',
    components: vueComponents,
    props: ['data', 'filter', 'searchText', 'showFilterColumns', 'showLegalOwner', 'eventBus'],
    data() {
      return {
        dataSource: null,
        actionDialog: {
          visible: false,
          type: null,
          page: 1
        },
        historyPopover: new ElhubPopoverModel({ width: 1100 }),
        eanSearchPopover: new ElhubPopoverModel(),
        totalsCount: {
          all: this.data.length,
          inSelection: null
        },
        eventsHandler: {
          onLoadComplete: () => {
            setTotalsInSelection(this.totalsCount, this.grid);
            setTimeout(() => updateSelectionLists(this));
          }
        },
        selection: {
          maxLimit:           1000,
          count:              0,
          requestAccessRows:  [],
          revokeAccessRows:   [],
          duplicateRows:      []
        },
        refNames: refNames
      };
    },
    computed: {
      computedFilter() {
        const filter = this.filter;
        return Object.keys(filter)
          .filter(key => filter[key] != null)
          .map(key => [key, 'anyof', filter[key]]);
      },
      grid() {
        return this.$refs[this.refNames.gridRefName].instance;
      },
      historyPopoverElem() {
        return this.$refs[this.refNames.historyPopoverRefName];
      },
      eanSearchPopoverElem() {
        return this.$refs[this.refNames.eanSearchPopoverRefName];
      },
      totalsCountString() {
        const all = this.totalsCount.all;
        const inSelection = this.totalsCount.inSelection;

        if (all == null || inSelection == null) return '';
        return `${inSelection} / ${all}`;
      },
      _searchText: {
        get() {
          return this.searchText;
        },
        set(value) {
          this.$emit('update:searchText', value);
        }
      },
      selectionRequestAccessBtnString() {
        return `Søk om tilgang (${this.selection.requestAccessRows.length} stk.)`;
      },
      selectionRevokeAccessBtnString() {
        return `Fjern tilgang (${this.selection.revokeAccessRows.length} stk.)`;
      },
      requestAccessRowsMaxLimitReached() {
        return this.selection.requestAccessRows.length > this.selection.maxLimit;
      },
      revokeAccessRowsMaxLimitReached() {
        return this.selection.revokeAccessRows.length > this.selection.maxLimit;
      },
      requestAccessBtnTooltipString() {
        return !this.requestAccessRowsMaxLimitReached ?
          'Søk om tilgang for valgte målere til Elhub' :
          `Over ${this.selection.maxLimit} målere er valgt. \nBegrens ditt utvalg for å fortsette.`;
      },
      revokeAccessBtnTooltipString() {
        return !this.revokeAccessRowsMaxLimitReached ?
          'Fjern tilgang for valgte målere i Elhub' :
          `Over ${this.selection.maxLimit} målere er valgt. \nBegrens ditt utvalg for å fortsette.`;
      },
      isEmployee() {
        return this.$user.type === 'EMPLOYEE';
      },
      isEmptyEANSearch() {
        return this.isEANSearch() && this.totalsCount.inSelection <= 1;
      }
    },
    methods: {
      isEANSearch() {
        return this.searchText != null && this.searchText.length === 18;
      },
      onGridContentReady() {
        this.eventsHandler.onLoadComplete();
      },
      onCellClick(event) {
        if (event.column.dataField === 'thirdPartyAccessStatusText') this.onEanCellClick(event);
      },
      onSelectionChanged(event) {
        updateSelectionLists(this);
      },
      onActionDialogConfirm() {
        const isWanted = this.actionDialog.type === enumActionType.REQUEST;
        const data = getSelectionTypeData(this).map(row => {
          return {
            ean:    row.ean,
            wanted: isWanted
          };
        });

        MeterStatusService.setAccessWantedStateOnMultipleItems(data)
          .then(() => {
            reloadDataSource(this);

            if (isWanted) this.actionDialog.page = 2;
            else {
              this.grid.clearSelection();
              hideConfirmActionDialog(this);
            }
          })
          .catch(error => {

            // TODO: Show error in confirm dialog, allowing user to retry
            console.error(error);
          });
      },
      onActionDialogCancel() {
        if (this.actionDialog.page === 2) {
          this.grid.clearSelection();
        }
        hideConfirmActionDialog(this);
      },
      onActionDialogSendMail(data) {
        MeterStatusService.sendMail(data)
          .then(res => {
            this.grid.clearSelection();
            hideConfirmActionDialog(this)
          })
          .catch(() => console.log(error));
      },
      onEanCellClick(event) {
        const data = event.data;
        if (data) {
          this.historyPopoverElem.update(event.event.target, () => {
            return MeterStatusService.getEanData(data.ean).then(res => res.elhubAccessLog);
          });
        }
      },
      onSearchByEanBtnClick(event) {
        this.eanSearchPopoverElem.update(event.target,  () => {
          return MeterStatusService.getEanData(this.searchText).then(res => res.meters);
        });
      },
      onAddMetersBtnClick() {
        setConfirmActionDialogType(enumActionType.REQUEST, this);
        generateListOfDuplicateMeters(this);
        showConfirmActionDialog(this);
      },
      onRevokeMetersBtnClick() {
        setConfirmActionDialogType(enumActionType.REVOKE, this);
        generateListOfDuplicateMeters(this);
        showConfirmActionDialog(this);
      },
      onSendMailBtnClick() {
        this.actionDialog.page = 2;

        setConfirmActionDialogType(enumActionType.MAIL, this);
        updateSelectionLists(this);
        showConfirmActionDialog(this);
      },
      getHistoryStatusTextByState(row) {
        switch (row.state) {
          case 'SUCCESS': return row.type === 'DELETE' ?
            'OK (Fjernet fra Elhub)' : row.type === 'METADATA' ?
            'OK (Periodisk tilgangskontroll)': 'OK';
          case 'REQUESTED': return 'Forespurt, venter på svar';
          case 'FAIL': return row.error;
        }

        return '?';
      },
      getHistoryStatusActionText(row) {
        return `${row.type}: ${row.state}`;
      },
      getDateString(value) {
        return dateService.getDateString(value);
      }
    },
    filters: {
      datedayofyear: function (value) {
        return dateService.getDateDayOfYear(value);
      },
      datetime: function (value) {
        return dateService.getDateTime(value);
      }
    },
    created() {
      if (this.eventBus) {
        this.eventBus.$on('$clearFilters', () => {
          if (this.grid) this.grid.clearFilter();
        });
      }
    },
    watch: {
      data() {
        this.dataSource = getDataSource(this.data);
      }
    },
    mounted() {
      this.dataSource = getDataSource(this.data);
    },
  };
</script>

<style scoped>
  .status-row-template {
    font-weight: 600;
    cursor:      pointer;
  }

  .grid-wrapper {
    position: relative;
  }

  @media all and (max-width: 1130px) {
    .grid-toolbar-wrapper {
      position: relative !important;
      margin:   15px 0 !important;
    }
  }

  .grid-toolbar-wrapper {
    position:    absolute;
    top:         10px;
    margin-left: 1px;
    z-index:     1;
  }

  .grid-toolbar-buttons-wrapper {
    display:      inline;
    margin-right: 20px;
  }

  .grid-toolbar-buttons-wrapper button + button {
    margin-left: 6px;
  }

  .grid-empty-result-wrapper {
    margin-top:       -1px;
    text-align:       center;
    background-color: #f3f3f3;
    padding:          40px 0;
    border:           1px solid #e6e6e6;
  }

  .slide-enter-active, .slide-leave-active {
    transition: all 0.4s cubic-bezier(0, 1, 0.5, 1);
  }

  .slide-enter, .slide-leave-to {
    margin-top: -134px;
  }

  .grid-empty-result-wrapper table {
    width: 100%;
  }

  .grid-empty-result-container{
    width:  400px;
    margin: 0 auto;
  }

  .grid-empty-result-icon {
    font-size:    45px;
    color:        hsl(192, 64%, 41%);
    margin-right: 20px;
  }

  .grid-empty-result-link-wrapper {
    margin-top: 12px;
    font-size:  14px;
  }

  .grid-empty-result-link {
    cursor: pointer;
    color:  blue;
  }

  .totals-count-wrapper {
    font-size:   12px;
    font-weight: 600;
  }

  .totals-count-value {
    font-size:   12px;
    font-weight: 500;
  }

  .history-popup-table {
    width:           100%;
    border-spacing:  0;
    border-collapse: collapse;
  }

  .history-popup-table thead th {
    text-align:     left;
    font-weight:    800;
    padding-bottom: 4px;
  }

  .history-popup-table tbody tr {
    border-top: 1px solid hsl(0, 0%, 91%);
  }

  .history-popup-table tbody td {
    padding: 4px 0;
  }

  .history-popup-table tbody td.datetime {
    font-size:   12px;
    font-weight: 600;
  }

  .history-popup-table tbody td:last-child, .history-popup-table thead th:last-child {
    padding-left: 7px;
  }

  .history-popup-table .icon {
    display:     block;
    padding:     0 10px 0 0;
    font-size:   9px;
    font-weight: 600;
  }

  .color-success {
    color: hsl(120, 100%, 25%);
  }

  .color-failure {
    color: hsl(0, 100%, 50%);
  }

  .color-link {
    color: hsl(240, 100%, 50%);
    text-decoration: none;
  }
</style>
<style>

  .grid-third-party-status-column {
    cursor: pointer;
  }

  i.dx-icon.dx-icon-export-excel-button {
    color: hsl(120, 87%, 30%);
  }
</style>
